import { Link } from 'react-router';
import { useHotkeys } from '@/lib/useHotkeys';
import { useScreenStore } from '@/state/screenStore';
import { useSettingsStore } from '@/state/settingsStore';
import { buttonClass } from '@/ui/button';
import { FullscreenIcon, TextLargerIcon, TextSmallerIcon } from '@/ui/icons';
import { Segmented } from '@/ui/Segmented';
import styles from './PlayerToolbar.module.css';

const VIEWS = [
  { value: 'timeline' as const, label: 'Timeline' },
  { value: 'grid' as const, label: 'Grid' },
  { value: 'stage' as const, label: 'Stage' },
  { value: 'neck' as const, label: 'Neck', ariaLabel: 'Neck (full fretboard or keyboard)' },
];

const WARNINGS = [
  { value: 0 as const, label: 'Off', ariaLabel: 'Off: no change warning' },
  { value: 1 as const, label: '1', ariaLabel: 'Warn 1 beat before a change' },
  { value: 2 as const, label: '2', ariaLabel: 'Warn 2 beats before a change' },
  { value: 4 as const, label: '4', ariaLabel: 'Warn 4 beats before a change' },
];

const MIN_SCALE = 0.8;
const MAX_SCALE = 1.4;

function toggleFullscreen() {
  if (document.fullscreenElement) void document.exitFullscreen();
  else void document.documentElement.requestFullscreen();
}

/** Back link and grid display options (F4.13; design: Player toolbar). */
export function PlayerToolbar() {
  const scale = useSettingsStore((s) => s.gridTextScale);
  const barsPerLine = useSettingsStore((s) => s.barsPerLine);
  const view = useSettingsStore((s) => s.playerView);
  const warnBeats = useSettingsStore((s) => s.changeWarningBeats);
  const update = useSettingsStore((s) => s.update);
  const showLibrary = useScreenStore((s) => s.showLibrary);
  const setScale = (next: number) => {
    update({ gridTextScale: Math.round(Math.min(MAX_SCALE, Math.max(MIN_SCALE, next)) * 10) / 10 });
  };

  useHotkeys({
    f: toggleFullscreen,
    '1': () => {
      update({ playerView: 'timeline' });
    },
    '2': () => {
      update({ playerView: 'grid' });
    },
    '3': () => {
      update({ playerView: 'stage' });
    },
    '4': () => {
      update({ playerView: 'neck' });
    },
  });

  return (
    <div className={styles.toolbar}>
      <Link to="/" className={buttonClass('ghost', styles.back)} onClick={showLibrary}>
        ← Library
      </Link>
      <Segmented
        label="View"
        options={VIEWS}
        value={view}
        onChange={(playerView) => {
          update({ playerView });
        }}
        tone="surface"
      />
      {view !== 'neck' && (
        <div className={styles.group}>
          <span className={styles.label}>Warn</span>
          <Segmented
            label="Change warning, beats ahead"
            options={WARNINGS}
            value={warnBeats}
            onChange={(changeWarningBeats) => {
              update({ changeWarningBeats });
            }}
            tone="surface"
          />
        </div>
      )}
      {view === 'grid' && (
        <div className={styles.group}>
          <span className={styles.label}>Text</span>
          <button
            type="button"
            className={styles.round}
            aria-label="Smaller text"
            disabled={scale <= MIN_SCALE}
            onClick={() => {
              setScale(scale - 0.1);
            }}
          >
            <TextSmallerIcon size={18} />
          </button>
          <button
            type="button"
            className={styles.round}
            aria-label="Larger text"
            disabled={scale >= MAX_SCALE}
            onClick={() => {
              setScale(scale + 0.1);
            }}
          >
            <TextLargerIcon size={22} />
          </button>
        </div>
      )}
      {view === 'grid' && (
        <label className={styles.group}>
          <span className={styles.label}>Bars per line</span>
          <select
            className={styles.select}
            value={barsPerLine}
            onChange={(e) => {
              update({ barsPerLine: Number(e.target.value) as 2 | 4 | 6 });
            }}
          >
            <option value={2}>2</option>
            <option value={4}>4</option>
            <option value={6}>6</option>
          </select>
        </label>
      )}
      <button
        type="button"
        className={styles.round}
        aria-label="Fullscreen"
        onClick={toggleFullscreen}
      >
        <FullscreenIcon size={18} />
      </button>
    </div>
  );
}
